import { useMemo, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';

import useFundContext from '@/contexts/Fund/useFundContext';
import { useGetAuthApi, usePostAuthApi } from '@/hooks/UseApiCalls';
import FormStepper from '@/components/ui/FormStepper/FormStepper';
import { StepDefinition } from '@/components/ui/FormStepper/types';
import { ActivityDomainDtoIn } from '@/models/in/ActivityDomainDtoIn';
import { ProjectTypeDtoIn } from '@/models/in/ProjectTypeDtoIn';
import { ProjectDtoIn } from '@/models/in/ProjectDtoIn';
import { ProjectDtoOut } from '@/models/out/ProjectDtoOut';
import { translateFileReadError } from '@/helpers/fileReaderHelper';
import {
  createProjectFormSchema,
  defaultProjectFormValues,
  ProjectFormData,
  OTHER_OPTION_ID,
} from './schemas/projectFormSchema';
import { mapFormDataToDto } from './utils/mapFormDataToDto';
import ProjectDescriptionStep from './steps/ProjectDescriptionStep';
import ProjectExpensesStep from './steps/ProjectExpensesStep';
import ProjectSubmissionStep from './steps/ProjectSubmissionStep';

interface NewProjectFormProps {
  onSuccess: () => void;
  /** Returns to the wizard's intro screen from step 1 ("חזרה להתחלה" in the design). */
  onBackToIntro: () => void;
}

/**
 * Shared frame for every step. The dialog is ~1400px wide and ~980px tall, so without this the
 * form sprawls across the full width (a one-line description stretching the whole screen) and
 * sits in the top third with hundreds of pixels of void beneath it. `max-w` holds a readable
 * measure, and `my-auto` centres the step vertically while still allowing a tall step to scroll.
 */
const StepFrame = ({ children }: { children: React.ReactNode }) => (
  <div className="w-full max-w-[760px] my-auto px-8 max-md:px-4 py-2">{children}</div>
);

/**
 * The 3-step body of the "פרויקט חדש" wizard. Per-step validation runs on that step's own
 * fields only (trigger), so a coordinator is never blocked by a field they have not reached.
 */
const NewProjectForm = ({ onSuccess, onBackToIntro }: NewProjectFormProps) => {
  const { t } = useTranslation();
  const { currentFund } = useFundContext();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const schema = useMemo(() => createProjectFormSchema(t), [t]);
  const methods = useForm<ProjectFormData>({
    resolver: zodResolver(schema),
    defaultValues: defaultProjectFormValues,
    mode: 'onTouched',
  });

  const { data: projectTypes, isLoading: isLoadingTypes } = useGetAuthApi<ProjectTypeDtoIn[]>(
    currentFund?.id ? `funds/${currentFund.id}/project-types` : ''
  );
  const { data: activityDomains } = useGetAuthApi<ActivityDomainDtoIn[]>(
    currentFund?.id ? `funds/${currentFund.id}/activity-domains` : ''
  );

  const { mutateAsync: createProject, isPending } = usePostAuthApi<ProjectDtoIn, ProjectDtoOut>(
    currentFund?.id ? `funds/${currentFund.id}/projects` : ''
  );

  const selectedTypeId = methods.watch('projectTypeId');
  const projectTypeLabel = selectedTypeId === OTHER_OPTION_ID
    ? methods.watch('projectTypeOther') ?? ''
    : projectTypes?.find((type) => type.id === selectedTypeId)?.name ?? '';

  const validateExpensesStep = async (): Promise<boolean> => {
    const values = methods.getValues();

    // Step 2 as a whole is optional (office decision, 2026-09-17), so an untouched step passes.
    // A partially filled row (a date typed, the cost left blank) is still a mistake, not an
    // intentionally empty row - blank rows are dropped on submit, so only these are flagged.
    const hasInvalidExpense = (values.expenses ?? []).some(
      (expense) => expense.validUntil && !((expense.amount ?? 0) > 0)
    );
    if (hasInvalidExpense) {
      setSubmitError(t('projects.wizard.validation.expenseAmountRequired'));
      return false;
    }

    const suppliers = values.suppliers ?? [];
    for (const supplier of suppliers) {
      if (!supplier.name?.trim()) {
        setSubmitError(t('projects.wizard.validation.supplierNameRequired'));
        return false;
      }
      if (!supplier.taxId?.trim()) {
        setSubmitError(t('projects.wizard.validation.supplierTaxIdRequired'));
        return false;
      }
      if (supplier.activityDomainId === null) {
        setSubmitError(t('projects.wizard.validation.activityDomainRequired'));
        return false;
      }
      if (supplier.activityDomainId === OTHER_OPTION_ID && !supplier.activityDomainOther?.trim()) {
        setSubmitError(t('projects.wizard.validation.activityDomainOtherRequired'));
        return false;
      }
    }

    setSubmitError(null);
    return true;
  };

  const steps: StepDefinition[] = useMemo(() => [
    {
      content: (
        <StepFrame>
          <ProjectDescriptionStep
            projectTypes={projectTypes ?? []}
            isLoadingTypes={isLoadingTypes}
          />
        </StepFrame>
      ),
      validate: () => methods.trigger(['name', 'projectTypeId', 'projectTypeOther', 'description']),
    },
    {
      content: (
        <StepFrame>
          <ProjectExpensesStep
            activityDomains={activityDomains ?? []}
            projectTypeLabel={projectTypeLabel}
          />
        </StepFrame>
      ),
      validate: validateExpensesStep,
    },
    {
      content: <StepFrame><ProjectSubmissionStep /></StepFrame>,
      validate: () => methods.trigger(['legalTermsApproved']),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [projectTypes, isLoadingTypes, activityDomains, projectTypeLabel, methods, t]);

  const handleSubmit = async () => {
    setSubmitError(null);
    try {
      const payload = await mapFormDataToDto(methods.getValues());
      await createProject(payload);
      onSuccess();
    } catch (error) {
      console.error('Error creating project:', error);
      // A file that could not be read gets its own message; anything else is a server error.
      const message = error instanceof Error && error.name.includes('File')
        ? translateFileReadError(error, t)
        : t('projects.wizard.errorCreating');
      setSubmitError(message);
    }
  };

  return (
    <FormProvider {...methods}>
      <FormStepper
        steps={steps}
        onSubmit={handleSubmit}
        isSubmitting={isPending}
        errorMessage={submitError}
        // The stepper's generic labels are "שליחה" / "חזרה לדף הקודם"; the design names the
        // final action and calls the back link a step, not a page.
        submitLabel={t('projects.wizard.step3.submit')}
        backLabel={t('projects.wizard.backToPrevious')}
        onFirstStepBack={onBackToIntro}
        firstStepBackLabel={t('projects.wizard.backToStart')}
        // "חזרה לשלב הקודם" wraps at the default 110px; widened only for this flow.
        backLinkWidthClass="w-[160px]"
      />
    </FormProvider>
  );
};

export default NewProjectForm;
